// OperationReport43Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

// ===== helpers =====
const groupBy = (arr, key) =>
  arr.reduce((a, it) => {
    const k = (it[key] ?? "").toString();
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every(r => (r[field] ?? "") === (rows[0]?.[field] ?? ""));

const N = v => Number(v ?? 0);
function fmtMoney(n) { const num = Number(String(n ?? 0).replace(/,/g, "")); return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v;
  const d = typeof v === "string" ? parseISO(v) : v;
  const be = addYears(543, d); const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
function formatMonthYearBE(date) { if (!date) return ""; const be = addYears(543, date); return format(be, "MMMM yyyy", { locale: th }); }
function pick(...c) { for (const x of c) { if (x !== undefined && x !== null && String(x).trim() !== "") return x; } return ""; }
function splitBranchLevel(name) {
  const s = String(name || "").trim();
  if (!s) return { branch: "-", level: "-" };
  const m = s.match(/ระดับ(?:ที่)?\s*([0-9]+)/i);
  const level = m ? m[1] : "-";
  const branch = s.replace(/\s*ระดับ(?:ที่)?\s*[0-9]+.*/i, "").trim() || s;
  return { branch, level };
}

// ===== PDF Thai Font =====
function base64FromArrayBuffer(buf) { const b = new Uint8Array(buf); const c = 0x8000; let s = ""; for (let i = 0; i < b.length; i += c) { s += String.fromCharCode.apply(null, b.subarray(i, i + c)); } return btoa(s); }
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([fetch("/fonts/Sarabun-Regular.ttf"), fetch("/fonts/Sarabun-Bold.ttf")]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf); const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF(); thaiFontLoaded = true;
}

export function OperationReport43Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // เงื่อนไขแสดงคอลัมน์หน่วยงาน
  const showDeptCol =
    String(user?.roldId ?? roleId) === "4" &&
    String(deptIdFilter || "all") === "all";

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map(d => ({ value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""), label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? "") }));
    if (roleId === "4") { setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]); setDeptIdFilter(p => p ?? "all"); }
    else if (roleId === "3") { const self = mapped.find(o => o.value === userDeptId) || { value: userDeptId, label: "หน่วยงานของฉัน" }; setDeptOptions([self]); setDeptIdFilter(userDeptId); }
    else { setDeptOptions(mapped); setDeptIdFilter(p => p || (mapped.find(o => o.value === userDeptId)?.value ?? "")); }
  }, [deptsData, roleId, userDeptId]);
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // โหลดข้อมูล
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation43", {
        method: "POST",
        headers: { token: user.token },
        body: fd
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      const mapped = raw.map(x => {
        const SupportNo = pick(x.SupportNo, x.supportNo);
        const SupportName = pick(x.SupportName, x.supportName);
        const DeptName = pick(x.DeptName, x.deptName); // <<< เพิ่ม DeptName
        const certNo = pick(x.SkillStandardCertNoRmth3, x.skillStandardCertNoRmth3, x.Certificate26No, x.certificate26No);
        const certDate = pick(x.CertificateDate, x.certificateDate, x.CertificateTestDate, x.certificateTestDate);
        const lawOcc = pick(x.LawOccupationName, x.lawOccupationName, x.TestOccupationName, x.testOccupationName);
        const { branch, level } = splitBranchLevel(lawOcc);
        const empTotal = pick(x.EmployeeTotal, x.employeeTotal);
        const personTotal = pick(x.PersonTotal, x.personTotal);
        const payAmt = pick(x.PaymentAmount, x.paymentAmount);
        const payDate = pick(x.PaymentDate, x.paymentDate);

        return {
          SupportNo,
          SupportName,
          DeptName,
          CertNo: certNo || "-",
          CertDate: certDate || "",
          Branch: branch,
          Level: level,
          EmployeeTotal: N(empTotal),
          PersonTotal: N(personTotal),
          PaymentAmount: N(String(payAmt ?? 0).replace(/,/g, "")),
          PaymentDate: payDate || ""
        };
      });

      setItems(mapped);
    } catch (e) {
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally { setLoading(false); }
  };

  // รวมยอดเงิน
  const totalPayment = items.reduce((s, r) => s + N(r.PaymentAmount), 0);

  // ===== Header 3 แถว (dynamic DeptName) =====
  const head1 = [
    "ที่",
    "เลขที่คำขอ",
    "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
    ...(showDeptCol ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 3 }] : []),
    { content: "การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ", colSpan: 4 },
    { content: "ดำเนินการทดสอบให้แก่ลูกจ้าง", colSpan: 2 },
    { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", rowSpan: 3 },
    { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
  ];
  const head2 = [
    "", "", ...(showDeptCol ? [""] : []),
    { content: "หนังสือรับรองเลขที่", rowSpan: 2 },
    { content: "ออกให้ ณ วันที่", rowSpan: 2 },
    { content: "สาขาอาชีพ", colSpan: 2 },
    { content: "ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)", rowSpan: 2 },
    { content: "จำนวนผู้เข้ารับการทดสอบ (คน)", rowSpan: 2 },
    "", "" // money/date rowSpan in head1
  ];
  const head3 = [
    "", "", ...(showDeptCol ? [""] : []),
    "", "", "สาขา", "ระดับ", "", "", "", ""
  ];

  // ดัชนีคอลัมน์ (leaf) แบบ dynamic
  const COL = showDeptCol
    ? {
        No: 0, SupportNo: 1, SupportName: 2, DeptName: 3,
        CertNo: 4, CertDate: 5, Branch: 6, Level: 7,
        Emp: 8, Person: 9,
        Money: 10, PayDate: 11,
      }
    : {
        No: 0, SupportNo: 1, SupportName: 2,
        CertNo: 3, CertDate: 4, Branch: 5, Level: 6,
        Emp: 7, Person: 8,
        Money: 9, PayDate: 10,
      };

  // ===== Build AoA & merges (dynamic DeptName) =====
  const buildAoaAndMerges = () => {
    const row1 = [
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),
      "การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ", "", "", "",
      "ดำเนินการทดสอบให้แก่ลูกจ้าง", "",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ];
    const row2 = [
      "", "", ...(showDeptCol ? [""] : []),
      "หนังสือรับรองเลขที่", "ออกให้ ณ วันที่", "สาขาอาชีพ", "",
      "ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)", "จำนวนผู้เข้ารับการทดสอบ (คน)",
      "", "",
    ];
    const row3 = [
      "", "", ...(showDeptCol ? [""] : []),
      "", "", "สาขา", "ระดับ", "", "", "", ""
    ];

    const aoa = [row1, row2, row3];
    const merges = [];

    // คอลัมน์ต้นคงที่ (No/SupportNo/SupportName[/DeptName]) rowSpan 3
    const fixed = showDeptCol ? 4 : 3;
    for (let c = 0; c < fixed; c++) merges.push({ s: { r: 0, c }, e: { r: 2, c } });

    // กลุ่ม "การรับรอง..." 4 คอลัมน์
    const certStart = fixed;
    merges.push({ s: { r: 0, c: certStart }, e: { r: 0, c: certStart + 3 } });
    // แถว 2: CertNo, CertDate rowSpan; "สาขาอาชีพ" colSpan 2
    merges.push({ s: { r: 1, c: certStart + 0 }, e: { r: 2, c: certStart + 0 } });
    merges.push({ s: { r: 1, c: certStart + 1 }, e: { r: 2, c: certStart + 1 } });
    merges.push({ s: { r: 1, c: certStart + 2 }, e: { r: 1, c: certStart + 3 } });

    // กลุ่ม "ดำเนินการทดสอบ..." 2 คอลัมน์
    const testStart = certStart + 4;
    merges.push({ s: { r: 0, c: testStart }, e: { r: 0, c: testStart + 1 } });
    merges.push({ s: { r: 1, c: testStart + 0 }, e: { r: 2, c: testStart + 0 } });
    merges.push({ s: { r: 1, c: testStart + 1 }, e: { r: 2, c: testStart + 1 } });

    // เงิน/วันที่จ่าย rowSpan 3
    const moneyCol = testStart + 2;
    const payDateCol = testStart + 3;
    merges.push({ s: { r: 0, c: moneyCol }, e: { r: 2, c: moneyCol } });
    merges.push({ s: { r: 0, c: payDateCol }, e: { r: 2, c: payDateCol } });

    // แถวข้อมูล
    let cur = 3;
    Object.entries(groupBy(items, "SupportNo")).forEach(([sn, rows], gIdx) => {
      const span = rows.length;
      const uPayAmt = isUniform(rows, "PaymentAmount");
      const uPayDt = isUniform(rows, "PaymentDate");

      rows.forEach((r, idx) => {
        const row = [];
        row[COL.No] = idx === 0 ? gIdx + 1 : "";
        row[COL.SupportNo] = idx === 0 ? (r.SupportNo || "-") : "";
        row[COL.SupportName] = idx === 0 ? (r.SupportName || "-") : "";
        if (showDeptCol) row[COL.DeptName] = idx === 0 ? (r.DeptName || "-") : "";

        row[COL.CertNo] = r.CertNo || "-";
        row[COL.CertDate] = toThaiDateLabel(r.CertDate);
        row[COL.Branch] = r.Branch || "-";
        row[COL.Level] = r.Level || "-";
        row[COL.Emp] = r.EmployeeTotal;
        row[COL.Person] = r.PersonTotal;

        row[COL.Money] = uPayAmt ? (idx === 0 ? r.PaymentAmount : "") : r.PaymentAmount;
        row[COL.PayDate] = uPayDt ? (idx === 0 ? toThaiDateLabel(r.PaymentDate) : "") : toThaiDateLabel(r.PaymentDate);

        // เติมช่องว่างให้ครบ
        for (let c = 0; c <= COL.PayDate; c++) if (row[c] === undefined) row[c] = "";

        aoa.push(row);
        cur++;
      });

      // Merge แนวตั้งสำหรับ SupportNo/Name/(DeptName)
      merges.push({ s: { r: cur - span, c: COL.SupportNo }, e: { r: cur - 1, c: COL.SupportNo } });
      merges.push({ s: { r: cur - span, c: COL.SupportName }, e: { r: cur - 1, c: COL.SupportName } });
      if (showDeptCol) {
        merges.push({ s: { r: cur - span, c: COL.DeptName }, e: { r: cur - 1, c: COL.DeptName } });
      }
      // Merge แนวตั้งสำหรับ Money/PayDate ถ้า uniform
      if (uPayAmt) merges.push({ s: { r: cur - span, c: COL.Money }, e: { r: cur - 1, c: COL.Money } });
      if (uPayDt) merges.push({ s: { r: cur - span, c: COL.PayDate }, e: { r: cur - 1, c: COL.PayDate } });
    });

    return { aoa, merges };
  };

  const handleExportExcel = () => {
    if (!items.length) { Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning"); return; }
    const { aoa, merges } = buildAoaAndMerges();
    const wb = XLSX.utils.book_new(); const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    ws["!cols"] = showDeptCol
      ? [
          { wch: 4 }, { wch: 18 }, { wch: 42 }, { wch: 28 },
          { wch: 24 }, { wch: 18 }, { wch: 28 }, { wch: 10 },
          { wch: 20 }, { wch: 22 },
          { wch: 22 }, { wch: 22 },
        ]
      : [
          { wch: 4 }, { wch: 18 }, { wch: 42 },
          { wch: 24 }, { wch: 18 }, { wch: 28 }, { wch: 10 },
          { wch: 20 }, { wch: 22 },
          { wch: 22 }, { wch: 22 },
        ];

    // footer sum
    const footerRow = aoa.length;
    ws[XLSX.utils.encode_cell({ r: footerRow, c: 0 })] = { t: "s", v: "รวมจำนวนเงินช่วยเหลือ" };
    ws[XLSX.utils.encode_cell({ r: footerRow, c: COL.Money })] = { t: "n", v: totalPayment };
    ws["!merges"].push({
      s: { r: footerRow, c: 0 },
      e: { r: footerRow, c: COL.Money - 1 }
    });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ประกาศ 5");
    const filename = `รายงาน_ประกาศ5_${reportMonth || "ทั้งหมด"}_${deptIdFilter}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleExportPDF = async () => {
    if (!items.length) { Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning"); return; }
    await ensureThaiFontLoaded();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(`รายงานการรับรองมาตรฐาน/การทดสอบ (ประกาศ 5) — ${formatMonthYearBE(reportMonthDate) || "ทุกเดือน"}`, 40, 40);

    const head = [
      head1.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      head2.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      head3.map(t => ({ content: t }))
    ];

    const body = items.map((r, idx) => {
      const row = [];
      row[COL.No] = idx + 1;
      row[COL.SupportNo] = r.SupportNo || "-";
      row[COL.SupportName] = r.SupportName || "-";
      if (showDeptCol) row[COL.DeptName] = r.DeptName || "-";
      row[COL.CertNo] = r.CertNo || "-";
      row[COL.CertDate] = toThaiDateLabel(r.CertDate);
      row[COL.Branch] = r.Branch || "-";
      row[COL.Level] = r.Level || "-";
      row[COL.Emp] = r.EmployeeTotal;
      row[COL.Person] = r.PersonTotal;
      row[COL.Money] = fmtMoney(r.PaymentAmount);
      row[COL.PayDate] = toThaiDateLabel(r.PaymentDate);
      for (let c = 0; c <= COL.PayDate; c++) if (row[c] === undefined) row[c] = "";
      return row;
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head, body, startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        [COL.SupportName]: { halign: "left" },
        ...(showDeptCol ? { [COL.DeptName]: { halign: "left" } } : {}),
        [COL.CertNo]: { halign: "left" },
        [COL.Branch]: { halign: "left" },
        [COL.Level]: { halign: "center" },
        [COL.Emp]: { halign: "right" },
        [COL.Person]: { halign: "right" },
        [COL.Money]: { halign: "right" },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(`รวมจำนวนเงินช่วยเหลือ: ${fmtMoney(totalPayment)} บาท`, 40, doc.lastAutoTable.finalY + 24);
    const filename = `รายงาน_ประกาศ5_${reportMonth || "all"}_${deptIdFilter}.pdf`;
    doc.save(filename);
  };

  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={opt => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`); setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else { setReportMonth(""); setReportMonthDate(null); }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={<input className="form-control rounded-pill text-center input-color" readOnly value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />}
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={3} className="text-center align-middle">ที่</th>
              <th rowSpan={3} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={3} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}
              <th colSpan={4} className="text-center align-middle">การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ</th>
              <th colSpan={2} className="text-center align-middle">ดำเนินการทดสอบให้แก่ลูกจ้าง</th>
              <th rowSpan={3} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th rowSpan={2} className="text-center">หนังสือรับรองเลขที่</th>
              <th rowSpan={2} className="text-center">ออกให้ ณ วันที่</th>
              <th colSpan={2} className="text-center">สาขาอาชีพ</th>
              <th rowSpan={2} className="text-center">ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)</th>
              <th rowSpan={2} className="text-center">จำนวนผู้เข้ารับการทดสอบ (คน)</th>
            </tr>
            <tr>
              <th className="text-center">สาขา</th>
              <th className="text-center">ระดับ</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDeptCol ? 12 : 11} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(([sn, rows], gIdx) => {
              const span = rows.length;
              const uPayAmt = isUniform(rows, "PaymentAmount");
              const uPayDt = isUniform(rows, "PaymentDate");

              return rows.map((r, idx) => (
                <tr key={`${sn}-${idx}`}>
                  <td className="text-center">{idx === 0 ? gIdx + 1 : ""}</td>
                  {idx === 0 && <td className="text-center" rowSpan={span}>{r.SupportNo || "-"}</td>}
                  {idx === 0 && <td className="text-start" rowSpan={span}>{r.SupportName || "-"}</td>}
                  {showDeptCol && idx === 0 && <td className="text-start" rowSpan={span}>{r.DeptName || "-"}</td>}

                  <td className="text-start">{r.CertNo || "-"}</td>
                  <td className="text-center">{toThaiDateLabel(r.CertDate)}</td>
                  <td className="text-start">{r.Branch || "-"}</td>
                  <td className="text-center">{r.Level || "-"}</td>
                  <td className="text-end">{r.EmployeeTotal}</td>
                  <td className="text-end">{r.PersonTotal}</td>

                  {uPayAmt
                    ? (idx === 0
                      ? <td className="text-end" rowSpan={span}>{fmtMoney(rows[0].PaymentAmount)}</td>
                      : null)
                    : <td className="text-end">{fmtMoney(r.PaymentAmount)}</td>}

                  {uPayDt
                    ? (idx === 0
                      ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(rows[0].PaymentDate)}</td>
                      : null)
                    : <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>}
                </tr>
              ));
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 10 : 9} className="text-end fw-bold">รวมจำนวนเงินช่วยเหลือ</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
